import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { supabase, createTimelineEvent } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Home, Plus, LogOut, User, FileText, ChevronRight, Clock, MapPin, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { SidebarLayout } from "@/components/SidebarLayout";

const stageLabels = {
  offer: "Offer & Negotiation",
  negotiation: "Offer & Negotiation",
  offer_negotiation: "Offer & Negotiation",
  in_contract: "In Contract",
  inspection: "Inspection & Remedy",
  remedy: "Inspection & Remedy",
  inspection_remedy: "Inspection & Remedy",
  appraisal: "Appraisal",
  closing: "Closing",
  completed: "Completed"
};

const stageBadgeStyles = {
  offer: "bg-blue-100 text-blue-700 border-blue-200",
  negotiation: "bg-blue-100 text-blue-700 border-blue-200",
  offer_negotiation: "bg-blue-100 text-blue-700 border-blue-200",
  in_contract: "bg-indigo-100 text-indigo-700 border-indigo-200",
  inspection: "bg-purple-100 text-purple-700 border-purple-200",
  remedy: "bg-purple-100 text-purple-700 border-purple-200",
  inspection_remedy: "bg-purple-100 text-purple-700 border-purple-200",
  appraisal: "bg-amber-100 text-amber-700 border-amber-200",
  closing: "bg-cyan-100 text-cyan-700 border-cyan-200",
  completed: "bg-green-100 text-green-700 border-green-200"
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, profile, logout, isAgent, displayName, displayEmail, displayRole } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // New transaction form
  const [newTransaction, setNewTransaction] = useState({
    property_address: "",
    city: "",
    state: "",
    zip: "",
    property_type: "single_family",
    price: "",
    buyer_email: "",
    seller_email: "",
    agent_side: ""
  });

  useEffect(() => {
    if (user) {
      console.log('[Dashboard] useEffect triggered, user.id:', user.id);
      fetchTransactions();
    } else {
      console.log('[Dashboard] useEffect triggered, no user — clearing');
      setTransactions([]);
      setLoading(false);
    }
  }, [user?.id]);

  // Hard safety: never spin longer than 8s
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(prev => {
        if (prev) {
          console.warn('[Dashboard] SAFETY: forcing loading=false at 8s');
          toast.error("Loading timed out. Showing available data.");
        }
        return false;
      });
    }, 8000);
    return () => clearTimeout(timer);
  }, []);

  const fetchTransactions = async () => {
    console.log('[Dashboard] fetchTransactions start, user.id:', user?.id, 'user.email:', user?.email);
    setLoading(true);
    try {
      // Step 1: Get participant rows for this user
      console.log('[Dashboard] Querying transaction_participants...');
      let participantTimeoutId;
      const participantResult = await Promise.race([
        supabase
          .from('transaction_participants')
          .select('transaction_id')
          .eq('user_id', user.id)
          .then(r => { clearTimeout(participantTimeoutId); return r; }),
        new Promise(resolve => {
          participantTimeoutId = setTimeout(() => {
            console.warn('[Dashboard] Participant query TIMED OUT (8s)');
            resolve({ data: [], error: null });
          }, 8000);
        })
      ]);

      console.log('[Dashboard] Participant result:', {
        count: participantResult.data?.length ?? 'null',
        error: participantResult.error || 'none'
      });

      if (participantResult.error) {
        console.error('[Dashboard] Participant query error:', participantResult.error);
        setTransactions([]);
        setLoading(false);
        return;
      }

      const transactionIds = (participantResult.data || []).map(p => p.transaction_id);
      console.log('[Dashboard] Transaction IDs:', transactionIds);

      if (transactionIds.length === 0) {
        console.log('[Dashboard] No transactions found for user');
        setTransactions([]);
        setLoading(false);
        return;
      }

      // Step 2: Fetch full transaction data
      console.log('[Dashboard] Querying transactions...');
      let txnTimeoutId;
      const txnResult = await Promise.race([
        supabase
          .from('transactions')
          .select('*')
          .in('id', transactionIds)
          .order('created_at', { ascending: false })
          .then(r => { clearTimeout(txnTimeoutId); return r; }),
        new Promise(resolve => {
          txnTimeoutId = setTimeout(() => {
            console.warn('[Dashboard] Transactions query TIMED OUT (8s)');
            resolve({ data: [], error: null });
          }, 8000);
        })
      ]);

      console.log('[Dashboard] Transactions result:', {
        count: txnResult.data?.length ?? 'null',
        error: txnResult.error || 'none'
      });

      if (txnResult.error) {
        console.error('[Dashboard] Transactions query error:', txnResult.error);
        setTransactions([]);
      } else {
        setTransactions(txnResult.data || []);
      }
    } catch (error) {
      console.error('[Dashboard] fetchTransactions THREW:', error);
      toast.error("Failed to load transactions");
      setTransactions([]);
    }
    // ALWAYS set loading false — not in finally, right here unconditionally
    setLoading(false);
    console.log('[Dashboard] fetchTransactions DONE, loading=false');
  };

  const handleCreateTransaction = async (e) => {
    e.preventDefault();
    if (!newTransaction.agent_side) {
      toast.error("Please select who the agent represents");
      return;
    }
    setCreating(true);
    
    let transactionId = null;
    
    try {
      // Step 1: Create the transaction (CRITICAL)
      console.log('[CreateTxn] Inserting transaction...');
      const { data: transaction, error: txnError } = await supabase
        .from('transactions')
        .insert({
          property_address: newTransaction.property_address,
          city: newTransaction.city,
          state: newTransaction.state,
          zip: newTransaction.zip,
          property_type: newTransaction.property_type,
          price: parseFloat(newTransaction.price.replace(/,/g, "")),
          stage: 'offer_negotiation',
          created_by: user.id,
          agent_side: newTransaction.agent_side
        })
        .select()
        .single();
      
      if (txnError) {
        console.error('[CreateTxn] Transaction insert failed:', txnError);
        throw txnError;
      }
      console.log('[CreateTxn] Transaction created:', transaction.id);
      transactionId = transaction.id;
      
      // Step 2: Add agent as participant (CRITICAL)
      console.log('[CreateTxn] Adding agent as participant...');
      const { error: agentError } = await supabase
        .from('transaction_participants')
        .insert({
          transaction_id: transaction.id,
          user_id: user.id,
          email: profile.email,
          role: 'agent',
          status: 'active'
        });
      
      if (agentError) {
        console.error('[CreateTxn] Agent participant insert failed:', agentError);
        throw agentError;
      }
      console.log('[CreateTxn] Agent participant added');

      // --- Non-critical steps below: failures logged but don't block navigation ---

      // Step 3: Timeline event (non-critical)
      try {
        await createTimelineEvent(
          transaction.id,
          'transaction_created',
          `Transaction created for ${newTransaction.property_address}`,
          {},
          user.id
        );
        console.log('[CreateTxn] Timeline event created');
      } catch (err) {
        console.error('[CreateTxn] Timeline event failed (non-blocking):', err);
      }
      
      // Step 4: Add buyer if provided (non-critical)
      if (newTransaction.buyer_email) {
        try {
          console.log('[CreateTxn] Looking up buyer profile...');
          const { data: buyerProfile } = await supabase
            .from('profiles')
            .select('id')
            .eq('email', newTransaction.buyer_email)
            .maybeSingle();
          
          const { error: buyerErr } = await supabase
            .from('transaction_participants')
            .insert({
              transaction_id: transaction.id,
              user_id: buyerProfile?.id || null,
              email: newTransaction.buyer_email,
              role: 'buyer',
              status: buyerProfile ? 'active' : 'pending'
            });
          
          if (buyerErr) console.error('[CreateTxn] Buyer insert failed:', buyerErr);
          else console.log('[CreateTxn] Buyer added');
          
          await createTimelineEvent(
            transaction.id,
            'participant_added',
            `Buyer invited: ${newTransaction.buyer_email}`,
            {},
            user.id
          );
        } catch (err) {
          console.error('[CreateTxn] Buyer add failed (non-blocking):', err);
        }
      }
      
      // Step 5: Add seller if provided (non-critical)
      if (newTransaction.seller_email) {
        try {
          console.log('[CreateTxn] Looking up seller profile...');
          const { data: sellerProfile } = await supabase
            .from('profiles')
            .select('id')
            .eq('email', newTransaction.seller_email)
            .maybeSingle();
          
          const { error: sellerErr } = await supabase
            .from('transaction_participants')
            .insert({
              transaction_id: transaction.id,
              user_id: sellerProfile?.id || null,
              email: newTransaction.seller_email,
              role: 'seller',
              status: sellerProfile ? 'active' : 'pending'
            });
          
          if (sellerErr) console.error('[CreateTxn] Seller insert failed:', sellerErr);
          else console.log('[CreateTxn] Seller added');
          
          await createTimelineEvent(
            transaction.id,
            'participant_added',
            `Seller invited: ${newTransaction.seller_email}`,
            {},
            user.id
          );
        } catch (err) {
          console.error('[CreateTxn] Seller add failed (non-blocking):', err);
        }
      }
      
      // Step 6: Create chat thread (non-critical)
      try {
        const { error: threadErr } = await supabase
          .from('chat_threads')
          .insert({
            transaction_id: transaction.id,
            name: 'General'
          });
        if (threadErr) console.error('[CreateTxn] Chat thread failed:', threadErr);
        else console.log('[CreateTxn] Chat thread created');
      } catch (err) {
        console.error('[CreateTxn] Chat thread failed (non-blocking):', err);
      }
      
      // SUCCESS: Navigate
      console.log('[CreateTxn] Complete. Navigating to transaction...');
      toast.success("Transaction created!");
      setCreateDialogOpen(false);
      setNewTransaction({
        property_address: "",
        city: "",
        state: "",
        zip: "",
        property_type: "single_family",
        price: "",
        buyer_email: "",
        seller_email: "",
        agent_side: ""
      });
      navigate(`/transaction/${transaction.id}`);
      
    } catch (error) {
      console.error('[CreateTxn] CRITICAL ERROR:', error);
      toast.error(error.message || "Failed to create transaction");
      // If transaction was created but a follow-up step failed critically,
      // still navigate to it rather than leaving user stuck
      if (transactionId) {
        toast.info("Transaction was created. Redirecting...");
        navigate(`/transaction/${transactionId}`);
      }
    } finally {
      setCreating(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('[Logout] Error:', error);
    }
    navigate("/");
    toast.success("Logged out successfully");
  };

  const formatPrice = (price) => {
    const num = typeof price === 'string' ? parseFloat(price.replace(/[$,]/g, '')) : price;
    if (isNaN(num) || num == null) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(num);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const propertyTypes = [
    { value: "single_family", label: "Single Family Home" },
    { value: "condo", label: "Condominium" },
    { value: "townhouse", label: "Townhouse" },
    { value: "multi_family", label: "Multi-Family" },
    { value: "land", label: "Land" },
    { value: "commercial", label: "Commercial" }
  ];

  const activeCount = transactions.filter(t => t.stage !== 'completed').length;
  const closingCount = transactions.filter(t => t.stage === 'closing').length;
  const completedCount = transactions.filter(t => t.stage === 'completed').length;

  return (
    <SidebarLayout activeNav="transactions">
      {/* Welcome Section */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight">
              Welcome back, {displayName?.split(' ')[0] || 'there'}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {isAgent 
                ? "Manage your real estate transactions and keep all parties informed."
                : "Track your transaction progress and communicate with your team."}
            </p>
          </div>
          {isAgent && (
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-md h-9 text-sm btn-press" data-testid="create-transaction-btn">
                  <Plus className="w-4 h-4 mr-1.5" />
                  New Transaction
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">Create Transaction</DialogTitle>
                  <DialogDescription>
                    Start a new real estate transaction
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateTransaction} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="property_address">Property Address *</Label>
                    <Input
                      id="property_address"
                      placeholder="123 Main Street"
                      value={newTransaction.property_address}
                      onChange={(e) => setNewTransaction({ ...newTransaction, property_address: e.target.value })}
                      required
                      data-testid="property-address-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        placeholder="Los Angeles"
                        value={newTransaction.city}
                        onChange={(e) => setNewTransaction({ ...newTransaction, city: e.target.value })}
                        required
                        data-testid="city-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">State *</Label>
                      <Input
                        id="state"
                        placeholder="CA"
                        maxLength={2}
                        value={newTransaction.state}
                        onChange={(e) => setNewTransaction({ ...newTransaction, state: e.target.value.toUpperCase() })}
                        required
                        data-testid="state-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="zip">ZIP *</Label>
                      <Input
                        id="zip"
                        placeholder="90001"
                        value={newTransaction.zip}
                        onChange={(e) => setNewTransaction({ ...newTransaction, zip: e.target.value })}
                        required
                        data-testid="zip-input"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="property_type">Property Type *</Label>
                      <Select 
                        value={newTransaction.property_type} 
                        onValueChange={(value) => setNewTransaction({ ...newTransaction, property_type: value })}
                      >
                        <SelectTrigger data-testid="property-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {propertyTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="price">Price *</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                        <Input
                          id="price"
                          className="pl-7"
                          placeholder="500,000"
                          inputMode="numeric"
                          value={newTransaction.price}
                          onChange={(e) => {
                            const digits = e.target.value.replace(/[^0-9]/g, "");
                            const formatted = digits ? digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "";
                            setNewTransaction({ ...newTransaction, price: formatted });
                          }}
                          required
                          data-testid="price-input"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Agent represents */}
                  <div className="space-y-2">
                    <Label>Agent Represents *</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { value: "seller", label: "Seller" },
                        { value: "buyer", label: "Buyer" },
                        { value: "both", label: "Both" },
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setNewTransaction({ ...newTransaction, agent_side: opt.value })}
                          className={`py-2.5 px-3 rounded-md text-sm font-medium border transition-colors ${
                            newTransaction.agent_side === opt.value
                              ? "bg-[#1A2B4F] text-white border-[#1A2B4F]"
                              : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                          }`}
                          data-testid={`create-agent-side-${opt.value}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="border-t pt-4 mt-4">
                    <p className="text-sm text-muted-foreground mb-3">Add participants (optional)</p>
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label htmlFor="buyer_email">Buyer Email</Label>
                        <Input
                          id="buyer_email"
                          type="email"
                          placeholder="buyer@example.com"
                          value={newTransaction.buyer_email}
                          onChange={(e) => setNewTransaction({ ...newTransaction, buyer_email: e.target.value })}
                          data-testid="buyer-email-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="seller_email">Seller Email</Label>
                        <Input
                          id="seller_email"
                          type="email"
                          placeholder="seller@example.com"
                          value={newTransaction.seller_email}
                          onChange={(e) => setNewTransaction({ ...newTransaction, seller_email: e.target.value })}
                          data-testid="seller-email-input"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={creating} data-testid="create-transaction-submit">
                      {creating ? "Creating..." : "Create Transaction"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
        <div className="bg-white p-5 rounded-lg border border-slate-100 shadow-soft">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">Active</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{activeCount}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white p-5 rounded-lg border border-slate-100 shadow-soft">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">In Closing</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{closingCount}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-cyan-50 flex items-center justify-center">
              <Clock className="w-5 h-5 text-cyan-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white p-5 rounded-lg border border-slate-100 shadow-soft">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">Completed</p>
              <p className="text-2xl font-bold text-slate-900 mt-1">{completedCount}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
              <Home className="w-5 h-5 text-emerald-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Transactions List */}
      <div className="bg-white rounded-lg border border-slate-100 shadow-soft">
        <div className="px-6 py-5 border-b border-slate-100">
          <h2 className="font-heading text-lg font-semibold text-slate-900">Your Transactions</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            {isAgent ? "All transactions you're managing" : "Transactions you're involved in"}
          </p>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">
                {isAgent 
                  ? "No transactions yet. Create your first one!"
                  : "You're not part of any transactions yet."}
              </p>
              {isAgent && (
                <Button 
                  className="mt-4 rounded-md text-sm"
                  onClick={() => setCreateDialogOpen(true)}
                  data-testid="empty-state-create-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Transaction
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {transactions.map((transaction) => (
                <div
                  key={transaction.id}
                  className="flex items-center justify-between p-4 rounded-lg border border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors group"
                  onClick={() => navigate(`/transaction/${transaction.id}`)}
                  data-testid={`transaction-card-${transaction.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center shrink-0">
                      <Home className="w-5 h-5 text-slate-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 truncate">
                        {transaction.property_address}, {transaction.city}, {transaction.state}
                      </p>
                      <div className="flex items-center gap-3 mt-0.5 text-sm text-slate-500">
                        <span>{formatPrice(transaction.price)}</span>
                        <span className="text-slate-300">|</span>
                        <span>Created {formatDate(transaction.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <Badge 
                      variant="outline" 
                      className={`${stageBadgeStyles[transaction.stage]} border text-xs`}
                    >
                      {stageLabels[transaction.stage]}
                    </Badge>
                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-slate-500 group-hover:translate-x-0.5 transition-all" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </SidebarLayout>
  );
}
